import dayjs from "dayjs";

/** ───────────── Helpers de normalización ───────────── **/
const norm = (v) => String(v ?? '');

// Robust key extractor for attractions/activities
export const getActivityKey = (a) =>
    norm(
        a?.id ??
        a?.activity_id ??
        a?.attraction_id ??
        a?.code ??
        a?.slug ??
        a?.name
    );

const ensureActivityObject = (a) => {
    // Si ya es objeto, sólo normalizamos su id y name
    if (a && typeof a === 'object') {
        const id = getActivityKey(a);
        const name = a.name ?? id;
        return { ...a, id, name };
    }
    // Si es string/number (una key), lo convertimos a objeto estándar
    const id = norm(a);
    return { id, name: id, value: 1 };
};

// Helper: given an extras-like object, return a normalized attractions array.
export const normalizeExtrasToAttractions = (extras) => {
    if (!extras) return [];
    // accept multiple shapes: prefer `selectedAttractions` (options payload), then `attractions`, then legacy `activities`
    const raw = Array.isArray(extras.selectedAttractions)
        ? extras.selectedAttractions
        : (Array.isArray(extras.attractions) ? extras.attractions : (Array.isArray(extras.activities) ? extras.activities : []));
    return raw.map(ensureActivityObject);
};

const normalizePaxText = (value) =>
    String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

const paxMatches = (ratePax, paxTitle) => {
    if (!ratePax) return false;
    const target = normalizePaxText(paxTitle);
    if (!target) return false;
    const candidates = Array.isArray(ratePax)
        ? ratePax.map(String)
        : (typeof ratePax === 'string' ? ratePax.split(',') : [ratePax]);
    return candidates.some((candidate) => {
        const c = normalizePaxText(candidate);
        if (!c) return false;
        return c === target || c.includes(target) || target.includes(c);
    });
};
const getRateKey = (ratePax) => {
    if (Array.isArray(ratePax)) return String(ratePax.join(','));
    return String(ratePax ?? '');
};
const getActivityRateSelectedQuantity = (activity, ratePax) => {
    const ratesMap = (activity && typeof activity === 'object' && activity.rate_quantities && typeof activity.rate_quantities === 'object')
        ? activity.rate_quantities
        : null;
    if (!ratesMap) return null;
    const key = getRateKey(ratePax);
    let raw = null;
    if (key in ratesMap) {
        raw = Number(ratesMap[key]);
    } else {
        const normalizedKey = normalizePaxText(key);
        const fallbackEntry = Object.entries(ratesMap).find(([storedKey]) => {
            const s = normalizePaxText(storedKey);
            return s === normalizedKey || s.includes(normalizedKey) || normalizedKey.includes(s);
        });
        if (!fallbackEntry) return null;
        raw = Number(fallbackEntry[1]);
    }
    if (!Number.isFinite(raw) || raw < 0) return null;
    return Math.floor(raw);
};

const getActivityRateSelectedQuantityByPaxType = (activity, paxType) => {
    const ratesMap = (activity && typeof activity === 'object' && activity.rate_quantities && typeof activity.rate_quantities === 'object')
        ? activity.rate_quantities
        : null;
    if (!ratesMap) return null;

    const target = normalizePaxText(paxType);
    if (!target) return null;

    const exactEntry = Object.entries(ratesMap).find(([storedKey]) => normalizePaxText(storedKey) === target);
    if (!exactEntry) return null;

    const raw = Number(exactEntry[1]);
    if (!Number.isFinite(raw) || raw < 0) return null;
    return Math.floor(raw);
};

/** ───────────── Funciones exportadas existentes ───────────── **/
export const getAssistantsString = (pax) => {
    return pax
        .filter(passenger => Number(passenger.value) > 0) // Ignorar si value es 0
        .map(passenger => `${passenger.value} ${passenger.title}${passenger.value > 1 ? 's' : ''}`)
        .join(', ');
};

// Nota: si tus objetos de pax traen "value" y no "total", quizá quieres usar value aquí.
// Lo dejo como estaba, pero considera cambiar currentValue.total -> currentValue.value.
export const getTotalAssistants = (pax) => {
    return pax.reduce((accumulator, currentValue) => accumulator + (Number(currentValue.total) || 0), 0);
};

/** ───────────── prePrareTourBookingData con IDs robustos ───────────── **/
export const prePrareTourBookingData = (tour, date, items, extras, options = {}) => {
    console.log('prePrareTourBookingData called with:', { tour, date, items, extras, options });
    const mergedOptions = {
        ...(tour?.options || {}),
        ...(options || {}),
    };

    const getCoverOrDefault = (array, condition) => {
        return array?.find?.(condition) || array?.[0] || null;
    };
    const condition = (image) => image?.isCover;
    const cover = getCoverOrDefault(tour?.images, condition);

    // 1) Fuente de attractions (selectedAttractions de options > extras.selectedAttractions > extras.attractions > extras.activities)
    const activitiesSourceRaw =
        (Array.isArray(mergedOptions?.selectedAttractions) && mergedOptions.selectedAttractions.length > 0)
            ? mergedOptions.selectedAttractions
            : (Array.isArray(extras?.selectedAttractions) && extras.selectedAttractions.length > 0)
                ? extras.selectedAttractions
                : (Array.isArray(extras?.attractions) && extras.attractions.length > 0 ? extras.attractions : (extras?.activities || []));

    // 2) Normalizamos SIEMPRE a objetos con id y name estables (string)
    const activitiesNormalized = (activitiesSourceRaw || []).map(ensureActivityObject);

    // 1b) Availability hours: prefer options, then extras, then tour
    const availabilitySource = mergedOptions?.availabilityHours ?? extras?.availabilityHours ?? tour?.availabilityHours;
    // Normalize to the same rough shape the UI expects: an array where the first element may be the day's hours array
    let normalizedAvailability = [];
    if (availabilitySource) {
        if (Array.isArray(availabilitySource)) {
            // keep as-is (could already be [hoursArray] or hoursArray)
            normalizedAvailability = availabilitySource;
        } else {
            // wrap single object/structure into an array to preserve compatibility
            normalizedAvailability = [availabilitySource];
        }
    }

    // Helper to pick the first hour string/object available
    const findFirstHour = (avail) => {
        if (!avail || avail.length === 0) return null;
        const firstContainer = Array.isArray(avail[0]) ? avail[0] : avail;
        if (!firstContainer || firstContainer.length === 0) return null;
        const first = firstContainer[0];
        // try to return a primitive hour (e.g. "09:00") or an object with .hour
        return (first && (first.hour || first.time)) ? (first.hour || first.time) : (typeof first === 'string' ? first : null);
    };
    const inferredFirstHour = findFirstHour(normalizedAvailability);

    // 3) Cálculo de total tolerante a estructuras
    const fullPaxCount = (Array.isArray(items) ? items : []).reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
    const totalPrice = (Array.isArray(items) ? items : []).reduce((total, item) => {
        const itemPrice = Number(item.price) || 0;
        const itemValue = Number(item.value) || 0;
        let itemTotal = itemPrice * itemValue;

        // Suma de extras por pax que aplica
        activitiesNormalized.forEach(activity => {
            const rawActivityQty = Number(activity?.quantity ?? activity?.value);
            const activityQty = Number.isFinite(rawActivityQty) && rawActivityQty > 0
                ? Math.floor(rawActivityQty)
                : (fullPaxCount > 0 ? fullPaxCount : 1);
            const rates = Array.isArray(activity?.rates) ? activity.rates : [];
            rates.forEach(rate => {
                const ratePrice = Number(rate?.price) || 0;
                if (paxMatches(rate?.pax, item?.title)) {
                    const selectedRateQty = getActivityRateSelectedQuantity(activity, rate?.pax);
                    const fallbackQtyFactor = fullPaxCount > 0 ? (activityQty / fullPaxCount) : 1;
                    const effectiveQty = selectedRateQty == null ? (itemValue * fallbackQtyFactor) : selectedRateQty;
                    itemTotal += (ratePrice * effectiveQty);
                }
            });
        });

        return total + itemTotal;
    }, 0);

    const languages = mergedOptions.languages ?? tour?.languages ?? [];
    const details = tour?.tourDetails ?? tour;

    const tourtoBook = {
        name: tour?.name,
        slug: tour?.slug,
        city: tour?.city?.slug,
        country: tour?.country?.slug,
        cover: cover?.imagen || cover, // mantiene comportamiento previo
        // ítems (pax) explícitos + legado booking
        items: items,
        booking: items,

        // 4) No guardar attractions dentro de `extras` (migrado a options.selectedAttractions)
        // Mantener compatibilidad poniendo las attractions dentro de options.selectedAttractions
        // También conservar/guardar availabilityHours y seleccionar la primera hora disponible si no viene en options
        options: {
            ...mergedOptions,
            selectedAttractions: activitiesNormalized,
            // keep provided availabilityHours if present; otherwise set normalizedAvailability
            availabilityHours: mergedOptions?.availabilityHours ?? (normalizedAvailability.length > 0 ? normalizedAvailability : undefined),
            // selectedHour: prefer explicit option, else inferred from availability
            selectedHour: mergedOptions?.selectedHour ?? inferredFirstHour,
        },

        // 5) Guarda tourDetails y alias "details"
        tourDetails: details,
        details: details,

        // idiomas
        languages: languages,

        date: dayjs(date).format('YYYY-MM-DD'),
        total: totalPrice,
        tax: tour?.tax ?? tour?.iva ?? 0,
        iva: tour?.tax,
    };

    // 6) Superficia customizations / top-level convenience según options
    const customizations = {
        ...(tour?.customizations || {}),
        ...(tourtoBook.customizations || {}),
    };

    if (mergedOptions?.selectedCompanion) {
        customizations.travelCompanion = mergedOptions.selectedCompanion;
        tourtoBook.travelCompanion = mergedOptions.selectedCompanion;
    }

    if (mergedOptions?.languages && Array.isArray(mergedOptions.languages) && mergedOptions.languages.length > 0) {
        customizations.language = mergedOptions.languages[0];
        tourtoBook.language = mergedOptions.languages[0];
    }

    if (mergedOptions?.selectedGuide) {
        customizations.guide = mergedOptions.selectedGuide;
        tourtoBook.guide = mergedOptions.selectedGuide;
    }

    if (mergedOptions?.selectedVehicle) {
        // conserva vehicle_option.value si venía
        customizations.vehicle = mergedOptions.selectedVehicle;
        tourtoBook.vehicle = mergedOptions.selectedVehicle;
    }

    if (mergedOptions?.selectedAttractions) {
        // guarda también en customizations normalizado para compatibilidad UI (no en extras)
        customizations.attractions = (mergedOptions.selectedAttractions || []).map(ensureActivityObject);
        tourtoBook.attractions = customizations.attractions;
    }

    if (mergedOptions?.selectedMeetingPoint || mergedOptions?.meetingPoint) {
        customizations.meetingPoint = mergedOptions?.selectedMeetingPoint || mergedOptions?.meetingPoint;
        tourtoBook.meetingPoint = customizations.meetingPoint;
    }

    if (Object.keys(customizations).length > 0) {
        tourtoBook.customizations = customizations;
    }

    return tourtoBook;
};

export const getTourSelectedAttractions = (tour = {}) => {
    const list =
        tour?.options?.selectedAttractions ??
        tour?.customizations?.attractions ??
        tour?.extras?.selectedAttractions ??
        tour?.extras?.attractions ??
        tour?.extras?.activities ??
        [];
    return normalizeExtrasToAttractions({ selectedAttractions: list });
};

export const getTourSelectedHour = (tour = {}) => (
    tour?.options?.selectedHour ??
    tour?.customizations?.schedule?.selectedHour ??
    tour?.customizations?.schedule ??
    tour?.selectedHour ??
    null
);

export const getTourSelectedMeetingPoint = (tour = {}) => (
    tour?.options?.selectedMeetingPoint ??
    tour?.options?.meetingPoint ??
    tour?.customizations?.meetingPoint ??
    tour?.customizations?.meetingpoint ??
    tour?.meetingPoint ??
    tour?.meetingpoint ??
    null
);

export const getTourSelectedCompanion = (tour = {}) => (
    tour?.options?.selectedCompanion ??
    tour?.customizations?.travelCompanion ??
    tour?.travelCompanion ??
    null
);

export const getTourSelectedGuide = (tour = {}) => (
    tour?.options?.selectedGuide ??
    tour?.customizations?.guide ??
    tour?.guide ??
    null
);

export const getTourSelectedLanguages = (tour = {}) => {
    if (Array.isArray(tour?.options?.languages)) return tour.options.languages;
    if (tour?.customizations?.language) return [tour.customizations.language];
    if (tour?.language) return [tour.language];
    return [];
};

export const getTourAvailabilityHours = (tour = {}) => (
    tour?.options?.availabilityHours ??
    tour?.availabilityHours ??
    null
);

export const getTourPricedOptions = (tour = {}) => (
    Array.isArray(tour?.pricedOptions)
        ? tour.pricedOptions
        : (Array.isArray(tour?.customizations?.pricedOptions) ? tour.customizations.pricedOptions : [])
);

export const normalizeCartTour = (tour = {}) => {
    const selectedAttractions = getTourSelectedAttractions(tour);
    const selectedHour = getTourSelectedHour(tour);
    const selectedMeetingPoint = getTourSelectedMeetingPoint(tour);
    const pricedOptions = getTourPricedOptions(tour);
    const availabilityHours = tour?.options?.availabilityHours ?? tour?.availabilityHours;

    const options = {
        ...(tour.options || {}),
        selectedAttractions,
        availabilityHours,
        ...(selectedMeetingPoint ? { selectedMeetingPoint } : {}),
        ...(selectedHour ? { selectedHour } : {}),
    };

    const customizations = {
        ...(tour.customizations || {}),
        attractions: selectedAttractions,
        pricedOptions,
        ...(selectedMeetingPoint ? { meetingPoint: selectedMeetingPoint } : {}),
        ...(selectedHour ? { schedule: { selectedHour } } : {}),
    };

    return {
        ...tour,
        code: tour?.code ?? tour?.tourDetails?.code ?? tour?.details?.code ?? null,
        options,
        customizations,
        pricedOptions,
        ...(selectedMeetingPoint ? { meetingPoint: selectedMeetingPoint } : {}),
        selectedHour: selectedHour ?? undefined,
        extras: {
            ...(tour.extras || {}),
            selectedAttractions,
        },
    };
};

const getActivityRatePriceForPaxType = (activity, paxType) => {
    const rates = Array.isArray(activity?.rates) ? activity.rates : [];
    const target = normalizePaxText(paxType);
    if (!target) return 0;

    const found = rates.find((rate) => {
        const candidates = Array.isArray(rate?.pax)
            ? rate.pax
            : (typeof rate?.pax === 'string' ? rate.pax.split(',') : [rate?.pax]);
        return candidates.some((candidate) => normalizePaxText(candidate) === target);
    });

    return Number(found?.price) || 0;
};

export const getTourPricingBreakdown = (tour = {}) => {
    const normalized = normalizeCartTour(tour);
    const rawTaxRate = Number(normalized?.tax ?? normalized?.iva) || 0;
    const ivaRate = rawTaxRate > 1 ? (rawTaxRate / 100) : rawTaxRate;

    const pax = (Array.isArray(normalized?.booking) ? normalized.booking : [])
        .filter((item) => (Number(item?.value) || 0) > 0)
        .map((item) => {
            const quantity = Number(item?.value) || 0;
            const unitPrice = Number(item?.price) || 0;
            return {
                pax_type: item?.title,
                quantity,
                unit_price: unitPrice,
                total: unitPrice * quantity,
            };
        });

    const paxSubtotal = pax.reduce((acc, item) => acc + (Number(item?.total) || 0), 0);
    const totalPaxQuantity = pax.reduce((acc, item) => acc + (Number(item?.quantity) || 0), 0);

    const activities = getTourSelectedAttractions(normalized).map((activity) => {
        const rawActivityQty = Number(activity?.quantity ?? activity?.value);
        const fallbackActivityQty = Number.isFinite(rawActivityQty) && rawActivityQty > 0
            ? Math.floor(rawActivityQty)
            : (totalPaxQuantity > 0 ? totalPaxQuantity : 1);

        const byPaxFromRates = (Array.isArray(activity?.rates) ? activity.rates : [])
            .map((rate) => {
                const unitPrice = Number(rate?.price) || 0;
                const selectedRateQty = getActivityRateSelectedQuantity(activity, rate?.pax);
                if (unitPrice <= 0 || selectedRateQty == null || selectedRateQty <= 0) return null;
                const paxLabel = Array.isArray(rate?.pax) ? rate.pax.join(', ') : String(rate?.pax ?? '');
                return {
                    pax_type: paxLabel || null,
                    quantity: selectedRateQty,
                    unit_price: unitPrice,
                    total: unitPrice * selectedRateQty,
                };
            })
            .filter(Boolean);

        // Merge duplicate pax labels if rate source contains repeated entries.
        const byPaxFromRatesMerged = Object.values(
            byPaxFromRates.reduce((acc, row) => {
                const key = String(row?.pax_type ?? '');
                if (!acc[key]) {
                    acc[key] = { ...row };
                } else {
                    const qty = (Number(acc[key].quantity) || 0) + (Number(row.quantity) || 0);
                    acc[key].quantity = qty;
                    acc[key].total = (Number(acc[key].unit_price) || 0) * qty;
                }
                return acc;
            }, {})
        );

        // console.log('pax: ', pax);
        // console.log('activity: ', activity);

        const byPaxFromTourPax =
            pax.map((paxItem) => {
                const unitPrice = getActivityRatePriceForPaxType(activity, paxItem.pax_type);
                const selectedRateQtyFromMap = getActivityRateSelectedQuantityByPaxType(activity, paxItem.pax_type);
                const selectedRateQty = selectedRateQtyFromMap == null ? paxItem.quantity : selectedRateQtyFromMap;
                if (selectedRateQty <= 0) return null;
                console.log('act pax type: ' + paxItem.pax_type + ', unitPrice: ' + unitPrice + ', selectedRateQty: ' + selectedRateQty);
                return {
                    pax_type: paxItem.pax_type,
                    quantity: selectedRateQty,
                    unit_price: unitPrice,
                    total: unitPrice * selectedRateQty,
                };
            })
                .filter(Boolean);
        // console.log('byPaxFromTourPax: ', byPaxFromTourPax);
        const byPax = byPaxFromTourPax;

        const totalFromRates = byPax.reduce((acc, item) => acc + (Number(item?.total) || 0), 0);
        const fallbackPrice = Number(activity?.price) || 0;
        const activityQuantity = byPax.reduce((acc, item) => acc + (Number(item?.quantity) || 0), 0) || fallbackActivityQty;
        const effectiveUnitPrice = totalFromRates > 0
            ? (activityQuantity > 0 ? (totalFromRates / activityQuantity) : totalFromRates)
            : fallbackPrice;
        const total = totalFromRates > 0 ? totalFromRates : (effectiveUnitPrice * activityQuantity);

        return {
            id: activity?.id ?? activity?.code ?? activity?.slug ?? activity?.name ?? null,
            name: activity?.name ?? activity?.label ?? activity?.title ?? null,
            code: activity?.code ?? null,
            // quantity: activityQuantity,
            // unit_price: effectiveUnitPrice,
            by_pax: byPax,
            total,
        };
    }).filter((activity) => (Number(activity?.total) || 0) > 0);

    const activitiesSubtotal = activities.reduce((acc, item) => acc + (Number(item?.total) || 0), 0);

    const extras = getTourPricedOptions(normalized)
        .filter((opt) => String(opt?.source || opt?.type || '').toLowerCase() !== 'attraction')
        .map((opt) => ({
            source: opt?.source || opt?.type || 'extra',
            label: opt?.label || opt?.name || opt?.title || 'Extra',
            price: Number(opt?.price) || 0,
        }))
        .filter((opt) => opt.price > 0);

    const extrasOptionsSubtotal = extras.reduce((acc, item) => acc + (Number(item?.price) || 0), 0);
    const extrasSubtotal = activitiesSubtotal + extrasOptionsSubtotal;
    const subtotal = paxSubtotal + extrasSubtotal;
    // const ivaTotal = ivaRate > 0 ? Math.ceil(subtotal * ivaRate) : 0;
    const ivaTotal = ivaRate > 0 ? (subtotal * ivaRate) : 0;
    const total = subtotal + ivaTotal;

    return {
        pax,
        activities,
        extras,
        totals: {
            pax_subtotal: paxSubtotal,
            extras_subtotal: extrasSubtotal,
            subtotal,
            iva_rate: rawTaxRate,
            iva_total: ivaTotal,
            total,
        },
    };
};

export const buildBookingPayloadFromCart = (tours = [], lead = {}) => {
    const leadPayload = {
        email: lead?.email,
        name: lead?.name,
        last_name: lead?.lastName,
        phone: lead?.phone,
    };

    const normalizedTours = (Array.isArray(tours) ? tours : []).map((tour) => normalizeCartTour(tour));
    const toursPayload = normalizedTours.map((tour) => {
        const breakdown = getTourPricingBreakdown(tour);
        const meetingPoint = getTourSelectedMeetingPoint(tour);
        const selectedHour = getTourSelectedHour(tour);
        const selectedCompanion = getTourSelectedCompanion(tour);
        const selectedGuide = getTourSelectedGuide(tour);
        const selectedLanguages = getTourSelectedLanguages(tour);
        const availabilityHours = getTourAvailabilityHours(tour);
        return {
            name: tour?.name ?? null,
            code: tour?.code ?? tour?.tourDetails?.code ?? tour?.details?.code ?? null,
            slug: tour?.slug ?? null,
            travel_date: tour?.date ?? null,
            lead: leadPayload,
            pax: breakdown.pax,
            activities: breakdown.activities,
            extras: breakdown.extras,
            totals: breakdown.totals,
            options: {
                selected_hour: selectedHour,
                meeting_point: meetingPoint,
                selected_companion: selectedCompanion,
                selected_languages: selectedLanguages,
                selected_guide: selectedGuide,
                // availability_hours: availabilityHours,
            },
        };
    });

    const subtotalTotal = toursPayload.reduce((acc, item) => acc + (Number(item?.totals?.subtotal) || 0), 0);
    const orderTotal = toursPayload.reduce((acc, item) => acc + (Number(item?.totals?.total) || 0), 0);
    const taxTotal = toursPayload.reduce((acc, item) => acc + (Number(item?.totals?.iva_total) || 0), 0);

    return {
        tours: toursPayload,
        totals: {
            subtotal_total: subtotalTotal,
            order_total: orderTotal,
            tax_total: taxTotal,
        },
    };
};
